import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { formatPrice } from '../lib/currency';
import { toast } from 'sonner';
import { Heart, Plane, Bell, Search, Trash2, MapPin, Loader2, AlertCircle, UserCog, Mail, Phone, ShieldCheck, Compass } from 'lucide-react';
import { fmtDateISO } from '../lib/dateFormat';

export default function Account() {
  const navigate = useNavigate();
  const { user, loading, checkAuth } = useAuth();
  const { currency } = useApp();
  const [saved, setSaved] = useState([]);
  const [guides, setGuides] = useState([]);
  const [trips, setTrips] = useState([]);
  const [searches, setSearches] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [resending, setResending] = useState(false);

  const resendVerification = async () => {
    setResending(true);
    try { const { data } = await api.resendVerification(); toast.success(data?.message || 'Verification email sent.'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not send email. Try again shortly.'); }
    finally { setResending(false); }
  };

  useEffect(() => {
    document.title = 'My Account | FlightDeals.in';
    if (!loading && !user) navigate('/login');
  }, [user, loading, navigate]);

  const load = () => {
    api.listSaved().then(({ data }) => setSaved(data.items)).catch(() => {});
    api.editorialSaved().then(({ data }) => setGuides(data.itineraries || [])).catch(() => {});
    api.listTrips().then(({ data }) => setTrips(data.trips)).catch(() => {});
    api.listSearches().then(({ data }) => setSearches(data.searches)).catch(() => {});
    api.listAlerts().then(({ data }) => setAlerts(data.alerts)).catch(() => {});
  };
  useEffect(() => { if (user) load(); }, [user]);
  // Refresh auth state on mount so a just-completed verification reflects immediately.
  useEffect(() => { checkAuth?.(); /* eslint-disable-next-line */ }, []);

  if (loading) return <Layout><div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div></Layout>;
  if (!user) return null;

  const removeAlert = async (id) => { await api.deleteAlert(id); setAlerts((a) => a.filter((x) => x.alert_id !== id)); toast.success('Alert removed'); };
  const removeSaved = async (id) => { await api.deleteSaved(id); setSaved((a) => a.filter((x) => x.item_id !== id)); toast.success('Removed'); };
  const unsaveGuide = async (slug) => { try { await api.editorialSave(slug); setGuides((g) => g.filter((x) => x.slug !== slug)); toast.success('Removed from saved guides'); } catch { toast.error('Failed'); } };

  const Empty = ({ icon: Icon, text }) => (
    <div className="text-center py-14 rounded-2xl border border-dashed border-border"><Icon className="h-9 w-9 mx-auto mb-2 opacity-40" /><p className="text-sm text-muted-foreground">{text}</p></div>
  );

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <span className="grid place-items-center h-12 w-12 rounded-2xl bg-accent text-accent-foreground text-lg font-semibold">{(user.name || 'U').charAt(0).toUpperCase()}</span>
          <div><h1 className="font-display text-2xl font-semibold">{user.name}</h1><p className="text-sm text-muted-foreground">{user.email}</p></div>
        </div>
        {user.email_verified === false && (
          <div className="mb-6 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 flex flex-wrap items-center gap-3" data-testid="verify-email-banner">
            <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
            <div className="flex-1 min-w-[220px]">
              <p className="font-medium text-sm">Please verify your email</p>
              <p className="text-xs text-muted-foreground">Confirm your email to save trips and set price alerts. Check your inbox for the link we sent to {user.email}.</p>
            </div>
            <Button size="sm" variant="outline" onClick={resendVerification} disabled={resending} data-testid="resend-verification-button">
              {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Resend email'}
            </Button>
          </div>
        )}
        <Tabs defaultValue="profile" data-testid="account-tabs">
          <TabsList className="flex flex-wrap h-auto">
            <TabsTrigger value="profile" data-testid="tab-profile"><UserCog className="h-4 w-4 mr-1.5" /> Profile</TabsTrigger>
            <TabsTrigger value="saved" data-testid="tab-saved"><Heart className="h-4 w-4 mr-1.5" /> Saved ({saved.length})</TabsTrigger>
            <TabsTrigger value="guides" data-testid="tab-guides"><Compass className="h-4 w-4 mr-1.5" /> Guides ({guides.length})</TabsTrigger>
            <TabsTrigger value="trips" data-testid="tab-trips"><MapPin className="h-4 w-4 mr-1.5" /> Trips ({trips.length})</TabsTrigger>
            <TabsTrigger value="searches" data-testid="tab-searches"><Search className="h-4 w-4 mr-1.5" /> Searches ({searches.length})</TabsTrigger>
            <TabsTrigger value="alerts" data-testid="tab-alerts"><Bell className="h-4 w-4 mr-1.5" /> Price alerts ({alerts.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-4">
            <ProfileSection user={user} onUpdated={checkAuth} />
          </TabsContent>

          <TabsContent value="saved" className="mt-4" data-testid="account-saved-list">
            {saved.length === 0 ? <Empty icon={Heart} text="No saved items yet. Tap the heart on any flight." /> : (
              <div className="space-y-3">{saved.map((s) => (
                <Card key={s.item_id} className="p-4 rounded-2xl flex items-center justify-between">
                  <div className="flex items-center gap-3"><Plane className="h-4 w-4 text-accent" /><div><p className="font-medium text-sm">{s.title}</p><p className="text-xs text-muted-foreground">{s.item_type} · {s.data?.price_inr ? formatPrice(s.data.price_inr, currency) : ''}</p></div></div>
                  <Button variant="ghost" size="icon" onClick={() => removeSaved(s.item_id)} data-testid={`remove-saved-${s.item_id}`}><Trash2 className="h-4 w-4" /></Button>
                </Card>
              ))}</div>
            )}
          </TabsContent>

          <TabsContent value="guides" className="mt-4" data-testid="account-guides-list">
            {guides.length === 0 ? <Empty icon={Compass} text="No saved guides yet. Tap Save on any itinerary guide." /> : (
              <div className="grid gap-3 sm:grid-cols-2">{guides.map((g) => (
                <Card key={g.slug} className="rounded-2xl overflow-hidden flex">
                  <div className="w-24 shrink-0 overflow-hidden bg-secondary"><img src={g.cover} alt={g.coverAlt} className="w-full h-full object-cover" /></div>
                  <div className="p-3 flex flex-col min-w-0 flex-1">
                    <p className="text-sm font-semibold leading-snug line-clamp-2">{g.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{g.destinationShort} · {g.durationDays}d</p>
                    <div className="mt-auto pt-2 flex items-center justify-between">
                      <Button size="sm" variant="outline" className="rounded-lg h-7 text-xs" onClick={() => navigate(`/itineraries/${g.slug}`)} data-testid={`open-guide-${g.slug}`}>Open guide</Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => unsaveGuide(g.slug)} data-testid={`unsave-guide-${g.slug}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </Card>
              ))}</div>
            )}
          </TabsContent>

          <TabsContent value="trips" className="mt-4">
            {trips.length === 0 ? <Empty icon={MapPin} text="No saved trips yet." /> : (
              <div className="space-y-3">{trips.map((t) => <Card key={t.trip_id} className="p-4 rounded-2xl"><p className="font-medium">{t.title}</p></Card>)}</div>
            )}
          </TabsContent>

          <TabsContent value="searches" className="mt-4">
            {searches.length === 0 ? <Empty icon={Search} text="No searches yet." /> : (
              <div className="space-y-3">{searches.map((s) => (
                <Card key={s.search_id} className="p-4 rounded-2xl flex items-center justify-between">
                  <div><p className="font-medium text-sm font-mono">{s.origin} → {s.destination}</p><p className="text-xs text-muted-foreground">{s.result_count} results · {fmtDateISO(s.created_at)}</p></div>
                  <Button variant="outline" size="sm" className="rounded-xl" onClick={() => navigate(`/flights/results?origin=${s.origin}&destination=${s.destination}&adults=1&cabin=economy`)} data-testid={`rerun-search-${s.search_id}`}>Search again</Button>
                </Card>
              ))}</div>
            )}
          </TabsContent>

          <TabsContent value="alerts" className="mt-4" data-testid="account-price-alerts">
            {alerts.length === 0 ? <Empty icon={Bell} text="No price alerts. Create one from any flight results page." /> : (
              <div className="space-y-3">{alerts.map((a) => (
                <Card key={a.alert_id} className="p-4 rounded-2xl flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Bell className="h-4 w-4 text-accent" />
                    <div><p className="font-medium text-sm font-mono">{a.origin} → {a.destination}</p><p className="text-xs text-muted-foreground">Target {formatPrice(a.target_price, currency)} · <Badge variant="outline" className="text-[10px]">{a.status}</Badge></p></div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => removeAlert(a.alert_id)} data-testid={`remove-alert-${a.alert_id}`}><Trash2 className="h-4 w-4" /></Button>
                </Card>
              ))}</div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}


function ProfileSection({ user, onUpdated }) {
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [saving, setSaving] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const googleLinked = !!(user?.google_sub || (user?.oauth_identities || []).some((i) => i.provider === 'google'));
  const connectGoogle = () => {
    // Same server-side flow; the callback links Google to this account by verified email.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const backend = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${backend}/api/auth/google/login?next=${encodeURIComponent('/account')}`;
  };

  useEffect(() => { setName(user?.name || ''); setPhone(user?.phone || ''); }, [user]);

  const save = async () => {
    if (!name || name.trim().length < 2) { toast.error('Please enter your name.'); return; }
    if (!phone || phone.trim().length < 8) { toast.error('Phone number is required.'); return; }
    setSaving(true);
    try {
      await api.updateProfile({ name: name.trim(), phone: phone.trim() });
      await onUpdated?.();
      toast.success('Profile updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save profile');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="profile-section">
      <Card className="p-5 rounded-2xl">
        <h2 className="font-semibold mb-4 flex items-center gap-2"><UserCog className="h-4 w-4 text-accent" /> Your details</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>Full name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="profile-name-input" />
          </div>
          <div>
            <Label>Phone number <span className="text-accent">*</span></Label>
            <div className="relative">
              <Phone className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98765 43210" className="pl-9" data-testid="profile-phone-input" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Required for trip updates. We won't share it.</p>
          </div>
        </div>
        <div className="mt-4">
          <Button onClick={save} disabled={saving} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="profile-save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Save changes
          </Button>
        </div>
      </Card>

      <Card className="p-5 rounded-2xl">
        <h2 className="font-semibold mb-3 flex items-center gap-2"><Mail className="h-4 w-4 text-accent" /> Email address</h2>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-medium text-sm" data-testid="profile-current-email">{user?.email}</p>
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              {user?.email_verified === false
                ? <><AlertCircle className="h-3.5 w-3.5 text-amber-500" /> Not verified</>
                : <><ShieldCheck className="h-3.5 w-3.5 text-accent" /> Verified</>}
            </p>
          </div>
          <Button variant="outline" className="rounded-xl" onClick={() => setEmailOpen(true)} data-testid="change-email-button">Change email</Button>
        </div>
      </Card>

      <EmailChangeDialog open={emailOpen} onOpenChange={setEmailOpen} onDone={onUpdated} />

      <Card className="p-5 rounded-2xl">
        <h2 className="font-semibold mb-3 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-accent" /> Connected sign-in</h2>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4" />
            <span className="text-sm">Google</span>
          </div>
          {googleLinked ? (
            <span className="text-xs text-accent flex items-center gap-1" data-testid="google-linked-badge"><ShieldCheck className="h-3.5 w-3.5" /> Connected</span>
          ) : (
            <Button variant="outline" size="sm" className="rounded-xl" onClick={connectGoogle} data-testid="connect-google-button">Connect Google</Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-2">Link Google to sign in faster. We match it to your account by your verified email.</p>
      </Card>
    </div>
  );
}

function EmailChangeDialog({ open, onOpenChange, onDone }) {
  const [step, setStep] = useState('request'); // request | verify
  const [newEmail, setNewEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setStep('request'); setNewEmail(''); setOtp(''); } }, [open]);

  const start = async () => {
    if (!newEmail || !newEmail.includes('@')) { toast.error('Enter a valid email'); return; }
    setBusy(true);
    try {
      const { data } = await api.emailChangeStart(newEmail.trim().toLowerCase());
      toast.success(data?.message || 'Code sent');
      if (data?.dev_otp) toast.info(`Dev code: ${data.dev_otp}`);
      setStep('verify');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send code');
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (!otp || otp.trim().length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setBusy(true);
    try {
      await api.emailChangeVerify(otp.trim());
      toast.success('Email updated');
      await onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verification failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="email-change-dialog">
        <DialogHeader><DialogTitle>Change your email</DialogTitle></DialogHeader>
        {step === 'request' ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">We'll send a 6-digit code to your new email to confirm you own it.</p>
            <div>
              <Label>New email</Label>
              <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="new@email.com" data-testid="new-email-input" />
            </div>
            <DialogFooter>
              <Button onClick={start} disabled={busy} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="send-otp-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Send code
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Enter the 6-digit code we sent to <span className="font-medium text-foreground">{newEmail}</span>.</p>
            <div>
              <Label>Verification code</Label>
              <Input inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))} placeholder="000000" className="tracking-[0.4em] text-center text-lg" data-testid="otp-input" />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-xl" onClick={() => setStep('request')} data-testid="otp-back-button">Back</Button>
              <Button onClick={verify} disabled={busy} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="verify-otp-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Verify &amp; update
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
